import {
  Divider,
  Drawer as MUIDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  type Theme,
  Toolbar,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { SwipeableList, SwipeableListItem } from "@sandstreamdev/react-swipeable-list";
import { type KeyboardEvent, type RefObject, useMemo } from "react";
import { DragDropContext, type DropResult } from "react-beautiful-dnd";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";

import Scrollbars from "@/Dashboard/components/Scrollbar";
import type { AssetTag, ContentType } from "@/Dashboard/pages/AssetLibrary/types";
import LeftHandMenu from "@/VisualBuilder/components/LeftHandMenu/LeftHandMenu";
import { useMenuRefs } from "@/VisualBuilder/hooks/useMenuRefs";
import { EXPERIENCE_PRIMARY_FILTER, PROPERTY_TIERS, TAB_STATUSES } from "@/constants";
import { useAccessControl } from "@/hooks/useAccessControl";
import { useTabStatus } from "@/hooks/useTabStatus";
import { useUrlParams } from "@/hooks/useUrlParams";
import { EyeClosed, Search } from "@/icons";
import { updateVisualBuilderData } from "@/store/actions";
import { useThunkDispatch } from "@/store/hooks";
import { currentExperienceSelector, simpleValuesSelector, visualBuilderDataSelector } from "@/store/selectors";
import type { VisualBuilderDataState } from "@/store/types";
import {
  onChangeContentSortOrder,
  onChangeTagSortOrder,
  onChangeTagStatus,
  onChangeTypeSortOrder,
  onChangeTypeStatus,
  onTagNameChangeComplete,
} from "@/utils/drawerUtils";

import SearchField from "./SearchField";
import AssetMenu from "../LeftHandMenu/AssetMenu";
import "../styles.css";
import SortedContentTypes from "./SortedContentTypes/SortedContentTypes";
import SortedTagList from "./SortedTagList/SortedTagList";

const drawerWidth = 240;

interface DrawerProps {
  readonly propertyId: number | string;
  readonly visualBuilderRef: RefObject<HTMLDivElement>;
  readonly startingAssetId: number | null;
}

interface StyleProps extends DrawerProps {
  color: string;
  isTabPresent: boolean;
  isPublicPageTab: boolean;
  isPublicPage: boolean;
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: theme.palette.common.black,
    border: "0",
    backdropFilter: "blur(5px)",
    overflowX: "hidden",
  },
  whitetext: {
    color: theme.palette.common.white,
    cursor: "pointer",
    "& > span": {
      fontSize: "16px",
    },
  },
  removeText: {
    fontSize: "14px",
    marginTop: "8px",
    padding: "0px 16px",
    color: theme.palette.semanticNeutral.dark,
    cursor: "pointer",
  },
  active: {
    fontWeight: "bold",
    borderBottom: "1px solid #FFF",
  },
  drawer: {
    position: "relative",
    zIndex: "1",
    "& .nobuttondisply": {
      display: "none",
    },
    "& .form-group .MuiFormControl-root": {
      width: "100%",
      textAlign: "left",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgb(255, 255, 255, .5)",
    },
    "& .MuiFormLabel-root": {
      color: "#FFF",
    },
  },
  toolbar: {
    minHeight: (props: StyleProps) =>
      props.isPublicPage ? "56px" : props.isTabPresent || props.isPublicPageTab ? "96px" : "136px",
  },
  leftMenuOptions: {
    marginTop: "40px",
    "& .MuiButtonBase-root  > div": {
      fontFamily: "Inter",
      fontSize: "16px",
      lineHeight: "22px",
      color: "#FFF",
    },
    "& .MuiListItemText-root": {
      "&.nospace": {
        display: "flex",
        flex: "0 1 auto",
        margin: "0 auto",
        justifyContent: "center",
      },
      "&.active": {
        color: "#71B6BC",
      },
    },
  },
  icon: {
    color: "#fff",
    marginRight: "0.75rem",
    fontSize: "0.75rem !important",
  },
  searchIcon: {
    fontSize: "1rem",
  },
  eyeClosed: {
    fontSize: "1.6rem",
    marginRight: "calc(.5rem + 3px)",
  },
  contentFilter: {
    height: "40px",
  },
  tab: {
    minWidth: "auto",
    color: "#fff",
    "&:focus-visible": {
      border: "1px solid white !important",
    },
    flex: "0 1 auto",
    margin: "0 auto",
    display: "flex",
    justifyContent: "center",
    textTransform: "lowercase",
    fontSize: "16px",
  },
  tabs: {
    width: "100%",
  },
  tabsIndicator: {
    height: "1px",
    bottom: "10px",
    backgroundColor: (props: StyleProps) => props.color,
  },
}));

export default function Drawer(props: DrawerProps) {
  const { propertyId, visualBuilderRef, startingAssetId } = props;

  const theme = useTheme();
  const params = useUrlParams();
  const dispatch = useThunkDispatch();
  const { canAccess } = useAccessControl();
  const { tabStatus } = params;
  const detailedTabStatus = useTabStatus();
  const { isTabCollect, isTabCustomize, isTabPresent, isTabPublicMediaShare, isPublicPageTab, isPropertyBasedTab } =
    detailedTabStatus;
  const { experience } = useSelector(currentExperienceSelector);
  const { "public-page": isPublicPage = false } = useSelector(simpleValuesSelector);
  const { visualBuilderData: baseVisualBuilderData } = useSelector(visualBuilderDataSelector(params)) as {
    visualBuilderData: VisualBuilderDataState & {
      assetCategoryTab: VisualBuilderDataState;
      contentTypeTab: VisualBuilderDataState;
    };
  };

  const { primaryFilter = EXPERIENCE_PRIMARY_FILTER.TAG, assetCategoryTab, contentTypeTab } = baseVisualBuilderData;
  const tabVisualBuilderData = useMemo(
    () => (primaryFilter === EXPERIENCE_PRIMARY_FILTER.TYPE ? contentTypeTab : assetCategoryTab),
    [primaryFilter, assetCategoryTab, contentTypeTab],
  );

  const publicVisualBuilderData = useMemo(
    () => ({
      ...baseVisualBuilderData,
      ...tabVisualBuilderData,
    }),
    [baseVisualBuilderData, tabVisualBuilderData],
  );

  const visualBuilderData = useMemo(
    () => (isPublicPageTab ? publicVisualBuilderData : baseVisualBuilderData),
    [isPublicPageTab, publicVisualBuilderData, baseVisualBuilderData],
  );

  const {
    contentTypes = [],
    filteredContentTypes = [],
    tags = [],
    filteredTags = [],
    tagMediaItems = {},
    filteredTagMediaItems = {},
    leftDrawer = true,
    rightDrawer = true,
    leftDrawerAutoHide = false,
    rightDrawerAutoHide = false,
    searchField,
    typeTagFilter,
    contentsOrdered = {},
    experienceContentTypeIds = {},
    experienceTagIds = {},
    contentTypesOrdered = {},
    tagsOrdered = {},
    hiddenContentTypes = {},
    hiddenTags = {},
    tagNames = {},
    typeFilterId,
    tagFilterId,
    gridView = false,
    locked = false,
    filteredContentTypeMediaItems = {},
    assetCategoriesToggled = {},
    assetCategoryFilterId,
    assetMenuOpen = false,
    currentMediaItemId,
    experienceAssetCategoryIds = {},
    filteredAssetCategoryMediaItems = {},
    hiddenAssetCategories = {},
    typesToggled = {},
    experienceMenuItems = [],
    propertyMenuItems = [],
    assetCategories = [],
    filteredAssetCategories = [],
  } = visualBuilderData;

  const isSalesPerson = !canAccess({
    allowedPermissions: ["experiences_template_action:delete"],
    allowedTiers: [PROPERTY_TIERS.PRO],
  }) as boolean;
  const isTemplate = experience?.isTemplate;

  const isLockedOrSalesTemplate = locked || (isSalesPerson && isTemplate);

  const color = (theme as unknown as { primaryColor: string }).primaryColor;

  const classes = useStyles({ ...props, color, isTabPresent, isPublicPageTab, isPublicPage });

  const menuRefs = useMenuRefs();
  const location = useLocation();

  const onSearchFieldToggle = () => {
    const values = { searchField: !searchField };
    dispatch(updateVisualBuilderData(params, values));
  };

  const onPrimaryFilterToggle = () => {
    if (isTabCustomize && isLockedOrSalesTemplate) return;

    const newFilter =
      primaryFilter === EXPERIENCE_PRIMARY_FILTER.TYPE ? EXPERIENCE_PRIMARY_FILTER.TAG : EXPERIENCE_PRIMARY_FILTER.TYPE;

    const values = {
      primaryFilter: newFilter,
      typeFilterId: null,
      tagFilterId: null,
    };
    dispatch(updateVisualBuilderData(params, values, {}, true));
  };

  const onTypeFilterToggle = () => {
    const values = { typeTagFilter: !typeTagFilter };
    dispatch(updateVisualBuilderData(params, values));
  };

  const hasPresentUpdatePermissions = canAccess({
    allowedPermissions: ["presentations:update"],
  });

  const isOrganizationsSection = location.pathname.startsWith("/organizations");
  const organizationPresentationMode = isOrganizationsSection && tabStatus === TAB_STATUSES.HOMEBASE;
  const isDraggable =
    (isTabCustomize || (isTabPresent && hasPresentUpdatePermissions)) && !organizationPresentationMode;

  const OnRenderBasedOnTags = (
    <SortedTagList
      tabStatus={tabStatus}
      locked={isLockedOrSalesTemplate}
      onTagNameChangeComplete={(tag: AssetTag, value: string) =>
        onTagNameChangeComplete({ tag, value, experienceTagIds, tagNames, dispatch, params })
      }
      onChangeContentSortOrder={(
        contentSortResult: {
          source: { droppableId: string; index: number };
          destination: { droppableId: string; index: number };
        },
        tagId: number,
      ) => {
        onChangeContentSortOrder({
          contentSortResult,
          tagId,
          mediaItems: tagMediaItems[tagId],
          filteredMediaItems: filteredTagMediaItems[tagId],
          filteredTagMediaItems,
          contentsOrdered,
          dispatch,
          params,
        });
      }}
      onChangeTagStatus={(tag: AssetTag) =>
        onChangeTagStatus({ tag, experienceTagIds, hiddenTags, dispatch, params, tagFilterId })
      }
      filteredTags={filteredTags}
      filteredTagMediaItems={filteredTagMediaItems}
      indexId={propertyId}
    />
  );

  const OnRenderBasedOnTypes = (
    <SortedContentTypes
      tabStatus={tabStatus}
      onChangeTypeStatus={(contentType: ContentType) =>
        onChangeTypeStatus({
          contentType,
          experienceContentTypeIds,
          hiddenContentTypes,
          dispatch,
          params,
          typeFilterId,
        })
      }
      filteredContentTypes={filteredContentTypes}
      indexId={propertyId}
      primaryFilter={primaryFilter}
      typeFilterId={typeFilterId}
      tagFilterId={tagFilterId}
      gridView={gridView}
      filteredContentTypeMediaItems={filteredContentTypeMediaItems}
      locked={isLockedOrSalesTemplate}
    />
  );

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    if (
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index === result.source.index
    ) {
      return;
    }
    if (result.destination.droppableId === "droppableTypes") {
      onChangeTypeSortOrder({
        typeSortResult: result,
        contentTypes,
        filteredContentTypes,
        experienceContentTypeIds,
        contentTypesOrdered,
        dispatch,
        params,
      });
    } else {
      onChangeTagSortOrder({
        tagSortResult: result,
        tags,
        filteredTags,
        experienceTagIds,
        tagsOrdered,
        dispatch,
        params,
      });
    }
  };

  const handlePrimaryFilterArrowKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      onPrimaryFilterToggle();
    }
  };

  const topMargin = useMemo(() => {
    if (isMobile) {
      return 30;
    }
    return 40;
  }, [isMobile]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {!leftDrawerAutoHide && (
        <MUIDrawer
          variant="persistent"
          anchor="left" // open
          open={leftDrawer}
          classes={{
            paper: classes.drawerPaper,
          }}
          className={classes.drawer}
          PaperProps={{
            id: "vb-drawer",
          }}
        >
          {/* Provides responsive height */}
          <Toolbar
            classes={{
              root: classes.toolbar,
            }}
          />
          <Scrollbars>
            <List
              disablePadding
              className={classes.leftMenuOptions}
              style={{
                width: isTabCustomize ? "auto" : "inherit",
                marginTop: "0px",
              }}
            >
              {isTabCustomize && !searchField && (
                <ListItemButton
                  onClick={onSearchFieldToggle}
                  style={{
                    border: 1,
                    borderStyle: "dashed",
                    borderColor: "#FFF",
                    opacity: 0.5,
                    padding: "16px",
                    width: `${drawerWidth}px`,
                  }}
                  disabled={isLockedOrSalesTemplate}
                >
                  <ListItemText primary="+ add search bar" className={classes.whitetext} />
                  <Search classes={{ root: classes.searchIcon }} />
                </ListItemButton>
              )}
              {(isTabPresent || isTabCollect || isTabPublicMediaShare || searchField) && (
                <div style={{ width: `${drawerWidth}px` }}>
                  <SwipeableList>
                    <SwipeableListItem
                      blockSwipe={isLockedOrSalesTemplate || !isTabCustomize}
                      swipeLeft={{
                        content: <EyeClosed className={classes.eyeClosed} />,
                        action: onSearchFieldToggle,
                      }}
                    >
                      <SearchField visualBuilderRef={visualBuilderRef} locked={isLockedOrSalesTemplate} />
                    </SwipeableListItem>
                  </SwipeableList>
                  {isTabCustomize && (
                    <ListItemButton
                      className={classes.removeText}
                      onClick={onSearchFieldToggle}
                      onKeyDown={onSearchFieldToggle}
                      disabled={isLockedOrSalesTemplate}
                    >
                      X remove search bar
                    </ListItemButton>
                  )}
                </div>
              )}
              {isTabCustomize && !typeTagFilter && (
                <ListItemButton
                  onClick={onTypeFilterToggle}
                  style={{
                    opacity: 0.5,
                    width: `${drawerWidth}px`,
                    padding: "16px",
                  }}
                  disabled={isLockedOrSalesTemplate}
                >
                  <ListItemText primary="+ add type filter" className={classes.whitetext} />
                </ListItemButton>
              )}
              {(isTabPresent || isTabCollect || isTabPublicMediaShare || typeTagFilter) && (
                <div style={{ width: `${drawerWidth}px`, marginBottom: "16px" }}>
                  <SwipeableList>
                    <SwipeableListItem
                      blockSwipe={isLockedOrSalesTemplate || !isTabCustomize}
                      swipeLeft={{
                        content: <EyeClosed className={classes.eyeClosed} />,
                        action: onTypeFilterToggle,
                      }}
                    >
                      <ListItem className={classes.contentFilter}>
                        <Tabs
                          value={primaryFilter === "type" ? 1 : 0}
                          textColor="inherit"
                          onChange={() => onPrimaryFilterToggle()}
                          aria-label="Tag Content Type Filter Tabs"
                          classes={{
                            root: classes.tabs,
                            indicator: classes.tabsIndicator,
                          }}
                        >
                          <Tab
                            classes={{ root: classes.tab }}
                            label="Category"
                            disabled={isLockedOrSalesTemplate && isTabCustomize}
                            disableRipple
                            style={{
                              color: primaryFilter === "tag" ? color : "#fff",
                              textTransform: "capitalize",
                            }}
                            data-analytics="vb-experience-category-tab"
                            tabIndex={0}
                            onKeyDown={(e) => handlePrimaryFilterArrowKeyDown(e)}
                          />
                          <Tab
                            classes={{ root: classes.tab }}
                            label="Type"
                            disabled={isLockedOrSalesTemplate && isTabCustomize}
                            disableRipple
                            style={{
                              color: primaryFilter === "type" ? color : "#fff",
                              textTransform: "capitalize",
                            }}
                            tabIndex={-1}
                            data-analytics="vb-experience-type-tab"
                            onKeyDown={(e) => handlePrimaryFilterArrowKeyDown(e)}
                          />
                        </Tabs>
                      </ListItem>
                    </SwipeableListItem>
                  </SwipeableList>
                  {isTabCustomize && (
                    <ListItemButton
                      className={classes.removeText}
                      onClick={onTypeFilterToggle}
                      onKeyDown={onTypeFilterToggle}
                      disabled={isLockedOrSalesTemplate}
                    >
                      X remove type filter
                    </ListItemButton>
                  )}
                </div>
              )}
              <LeftHandMenu
                assetCategories={assetCategories}
                assetCategoriesToggled={assetCategoriesToggled}
                assetCategoryFilterId={assetCategoryFilterId}
                assetMenuOpen={assetMenuOpen}
                experienceAssetCategoryIds={experienceAssetCategoryIds}
                experienceContentTypeIds={experienceContentTypeIds}
                filteredAssetCategories={filteredAssetCategories}
                filteredAssetCategoryMediaItems={filteredAssetCategoryMediaItems}
                filteredContentTypeMediaItems={filteredContentTypeMediaItems}
                filteredContentTypes={filteredContentTypes}
                hiddenAssetCategories={hiddenAssetCategories}
                hiddenContentTypes={hiddenContentTypes}
                locked={isLockedOrSalesTemplate}
                primaryFilter={primaryFilter}
                typeFilterId={typeFilterId}
                typesToggled={typesToggled}
                experienceMenuItems={experienceMenuItems}
                propertyMenuItems={propertyMenuItems}
                isDraggable={isDraggable}
                menuRefs={menuRefs}
                startingAssetId={startingAssetId}
              />
            </List>
          </Scrollbars>
        </MUIDrawer>
      )}
      <AssetMenu
        menuItems={isPropertyBasedTab ? propertyMenuItems : experienceMenuItems}
        fallbackMenuItems={!isPropertyBasedTab ? propertyMenuItems : []}
        assetCategoryFilterId={assetCategoryFilterId}
        assetMenuOpen={assetMenuOpen}
        currentMediaItemId={currentMediaItemId}
        drawerWidth={drawerWidth}
        filteredAssetCategoryMediaItems={filteredAssetCategoryMediaItems || []}
        filteredContentTypeMediaItems={filteredContentTypeMediaItems || []}
        gridView={gridView}
        leftDrawerOpen={!leftDrawerAutoHide && leftDrawer}
        primaryFilter={primaryFilter}
        typeFilterId={typeFilterId}
        isDraggable={isDraggable}
        menuRefs={menuRefs}
        startingAssetId={startingAssetId}
      />
      {!rightDrawerAutoHide && (
        <MUIDrawer
          className={classes.drawer}
          variant="persistent"
          anchor="right"
          open={rightDrawer}
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <Toolbar />
          {(!isPublicPageTab || isTabPresent) && <Toolbar />}
          <Divider />
          <Scrollbars
            className={classes.drawerMobHeight}
            style={{
              height: "100%",
              marginTop: isTabPresent || isPublicPageTab ? 0 : topMargin,
            }}
          >
            {primaryFilter === "tag" ? OnRenderBasedOnTypes : OnRenderBasedOnTags}
          </Scrollbars>
        </MUIDrawer>
      )}
    </DragDropContext>
  );
}
